import axios from 'axios';
import { ApiResponse, ApiError } from '../types/api';
import { getIPInfoString, getDynamicPlatform } from '../utils/clientInfo';
import { getOrCreateDeviceFingerprint } from '../utils/deviceFingerprint';

const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001',
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000', 10),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Language': 'en-US',
  },
};

export class ApiService {
  private api: any;
  private token: string | null = null;

  constructor(configOverrides: Partial<typeof API_CONFIG> = {}) {
    this.api = axios.create({ ...API_CONFIG, ...configOverrides });
    this.setupInterceptors();
    this.loadTokenFromStorage();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.api.interceptors.request.use(
      async (config: any) => {
        // Always get the latest token from localStorage to ensure it's up to date
        const token = localStorage.getItem('authToken');
        if (token) {
          // Check if token already has Bearer prefix
          const tokenValue = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
          config.headers.Authorization = tokenValue;
        }
        
        // Add language header
        config.headers.Language = 'en-US';

        // Device fingerprint (anti multi-account / risk scoring)
        try {
          const fp = await getOrCreateDeviceFingerprint();
          if (fp) config.headers['X-Device-Fingerprint'] = fp;
          config.headers['X-Device-Fingerprint-Version'] = 'v1';
        } catch {
          // ignore
        }
        
        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response: any) => {
        return response;
      },
      (error: any) => {
        // Handle 401 errors (unauthorized)
        if (error.response?.status === 401) {
          // Check if user was authenticated before clearing token
          const wasAuthenticated = !!localStorage.getItem('authToken');
          // Check if this is a duplicate login scenario (we're already handling it)
          const isDuplicateLogin = localStorage.getItem('duplicateLoginHandled') === 'true';
          this.clearToken();
          // Only redirect to login if user was previously authenticated and not handling duplicate login
          // This prevents redirects for public API calls that don't require auth
          // Also skip redirect if duplicate login is being handled (will redirect to / instead)
          if (wasAuthenticated && !isDuplicateLogin && !window.location.pathname.includes('/login') && !window.location.pathname.includes('/signup')) {
            window.location.href = '/';
          }
          // Clear the duplicate login flag after handling
          if (isDuplicateLogin) {
            localStorage.removeItem('duplicateLoginHandled');
          }
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: any): ApiError & Error {
    const message =
      error.response?.data?.message?.warning ||
      error.response?.data?.message?.error ||
      error.response?.data?.message?.success ||
      error.message ||
      'An error occurred';

    const apiError = Object.assign(new Error(message), {
      status: error.response?.status,
      code: error.code,
      response: error.response?.data,
    } as ApiError);

    return apiError as ApiError & Error;
  }

  private loadTokenFromStorage(): void {
    this.token = localStorage.getItem('authToken');
  }

  public setToken(token: string): void {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  public clearToken(): void {
    this.token = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
  }

  public getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  // Generic API methods
  public async get<T = any>(url: string, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Public API call without authentication
  public async postPublic<T = any>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.post(url, data, {
        ...config,
        headers: {
          ...config?.headers,
          Language: 'en-US',
          // Don't include Authorization header for public calls
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async post<T = any>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async postRaw<T = any>(url: string, data?: any, config?: any): Promise<T> {
    try {
      const response = await this.api.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getRaw<T = any>(url: string, config?: any): Promise<T> {
    try {
      const response = await this.api.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async put<T = any>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async delete<T = any>(url: string, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Utility method to add common request data
  public async addCommonData(data: any = {}): Promise<any> {
    const commonData = {
      ...data,
      platform: getDynamicPlatform(),
      ipADD: await getIPInfoString(),
    };
    return commonData;
  }

  // Method to check if user is authenticated
  public isAuthenticated(): boolean {
    // Always check localStorage for the latest token
    const token = localStorage.getItem('authToken');
    // Update internal token for consistency
    this.token = token;
    return !!token;
  }

  // Method to get stored user data
  public getStoredUserData(): any {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  }

  // Method to store user data
  public storeUserData(userData: any): void {
    localStorage.setItem('userData', JSON.stringify(userData));
  }

  public getStoredAllUserData(): any {
    const allUserData = localStorage.getItem('allUserData');
    return allUserData ? JSON.parse(allUserData) : null;
  }
  
  public storeAllUserData(allUserData: any): void {
    localStorage.setItem('allUserData', JSON.stringify(allUserData));
  }

  // Method to clear all stored data
  public clearAllData(): void {
    this.clearToken();
    localStorage.removeItem('bettingItem');
    localStorage.removeItem('eventkey');
    localStorage.removeItem('allUserData');
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
